import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@repo/ui";
import { useEffect, useRef, useState } from "react";

import AdminNav from "@/pages/admin/admin-nav";
import RegistrationModal from "@/pages/admin/registration-modal";

export default function AdminScanner() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [supported, setSupported] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [itemId, setItemId] = useState<number | null>(null);
  const [manualId, setManualId] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // @ts-expect-error experimental
    setSupported(typeof window !== "undefined" && !!window.BarcodeDetector);
  }, []);

  useEffect(() => {
    if (!scanning || !supported) return;
    let active = true;
    // Use loose typing for experimental API wrapper
    let detector:
      | { detect: (video: HTMLVideoElement) => Promise<Array<{ rawValue?: string }>> }
      | undefined;
    let stream: MediaStream;
    const start = async () => {
      try {
        // @ts-expect-error experimental
        detector = new window.BarcodeDetector({ formats: ["qr_code"] });
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        const tick = async () => {
          if (!active || !videoRef.current) return;
          try {
            const detections = await (
              detector as {
                detect: (video: HTMLVideoElement) => Promise<Array<{ rawValue?: string }>>;
              }
            ).detect(videoRef.current);
            if (detections && detections[0]?.rawValue) {
              setResult(detections[0].rawValue);
              setScanning(false);
              stop();
              return;
            }
          } catch {
            // ignore per-frame detection errors
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      } catch {
        setSupported(false);
        setScanning(false);
      }
    };
    const stop = () => {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
    start();
    return () => {
      active = false;
      stop();
    };
  }, [scanning, supported]);

  useEffect(() => {
    if (!result) return;
    const id = Number(result);
    if (!id) return;
    setItemId(id);
    setOpen(true);
  }, [result]);

  const onManualLookup = async () => {
    setResult(manualId);
  };

  return (
    <div>
      <AdminNav />
      <div className="p-6 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>QR Scanner</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {supported ? (
              <div className="space-y-3">
                <video ref={videoRef} className="w-full max-w-lg rounded" />
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={() => setScanning((s) => !s)}>
                    {scanning ? "Stop" : "Start"} scanning
                  </Button>
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const url = URL.createObjectURL(file);
                          const img = new Image();
                          img.src = url;
                          await new Promise((res, rej) => {
                            img.onload = () => res(null);
                            img.onerror = () => rej(new Error("Image load error"));
                          });
                          // @ts-expect-error experimental
                          const detector = new window.BarcodeDetector({ formats: ["qr_code"] }) as {
                            detect: (
                              image: HTMLImageElement
                            ) => Promise<Array<{ rawValue?: string }>>;
                          };
                          const detections = await detector.detect(img);
                          if (detections && detections[0]?.rawValue) {
                            setResult(detections[0].rawValue);
                          }
                          URL.revokeObjectURL(url);
                        } catch {
                          // ignore detection failure
                        }
                        // reset input value to allow re-uploading same file
                        e.currentTarget.value = "";
                      }}
                    />
                    <Button asChild variant="outline">
                      <span>Upload photo</span>
                    </Button>
                  </label>
                  {result && <div className="text-sm">Scanned: {result}</div>}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  QR scanning not supported on this device. Use upload or manual lookup below.
                </div>
                <label className="inline-flex">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      try {
                        const url = URL.createObjectURL(file);
                        const img = new Image();
                        img.src = url;
                        await new Promise((res, rej) => {
                          img.onload = () => res(null);
                          img.onerror = () => rej(new Error("Image load error"));
                        });
                        // @ts-expect-error experimental
                        const detector = new window.BarcodeDetector({ formats: ["qr_code"] }) as {
                          detect: (
                            image: HTMLImageElement
                          ) => Promise<Array<{ rawValue?: string }>>;
                        };
                        const detections = await detector.detect(img);
                        if (detections && detections[0]?.rawValue) {
                          setResult(detections[0].rawValue);
                        }
                        URL.revokeObjectURL(url);
                      } catch {
                        // ignore detection failure
                      }
                      e.currentTarget.value = "";
                    }}
                  />
                  <Button asChild variant="outline">
                    <span>Upload photo</span>
                  </Button>
                </label>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="manualId">Manual ID</Label>
              <div className="flex gap-2">
                <Input
                  id="manualId"
                  value={manualId}
                  onChange={(e) => setManualId(e.target.value)}
                  className="max-w-xs"
                />
                <Button onClick={onManualLookup}>Lookup</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <RegistrationModal open={open} onOpenChange={setOpen} itemId={itemId} />
      </div>
    </div>
  );
}
